import React, { createContext, useContext, useState } from 'react';
import { impersonateUser as startImpersonation, revokeImpersonationSession, signOutImpersonatedStaff } from './services/impersonationService';
import { adminLocationHref } from '../config/adminHost';
import { ADMIN_PATHS } from '../constants/adminPaths';
import ImpersonationStartOverlay from './components/ImpersonationStartOverlay';
import {
  getImpersonationTargetLabel,
  getImpersonationTargetMeta,
  markImpersonationExiting,
  clearImpersonationExiting,
  setImpersonationSession,
} from '../../impersonation/utils/impersonationSession';
import {
  waitForImpersonationExitMin,
  withImpersonationReturnParam,
  markImpersonationLoaderStarted,
} from '../../impersonation/exit/impersonationExit';

const ImpersonationContext = createContext();

export const useImpersonation = () => {
  const context = useContext(ImpersonationContext);
  if (!context) {
    throw new Error('useImpersonation must be used within an ImpersonationProvider');
  }
  return context;
};

export const ImpersonationProvider = ({ children }) => {
  const [impersonatedUser, setImpersonatedUser] = useState(() => {
    if (typeof window === 'undefined') return null;
    const meta = getImpersonationTargetMeta();
    if (!meta.userId) return null;
    return {
      userId: meta.userId,
      companyId: meta.companyId || null,
      claims: null,
      target: meta,
    };
  });
  const [loading, setLoading] = useState(false);
  const [handoffPhase, setHandoffPhase] = useState(null);
  const [error, setError] = useState(null);

  const impersonateUser = async (userId, companyId) => {
    const startedAt = markImpersonationLoaderStarted();
    try {
      setLoading(true);
      setHandoffPhase('starting');
      setError(null);

      const result = await startImpersonation(userId, companyId);

      if (!result.success) {
        throw new Error(result.error || 'Impersonation failed');
      }

      setImpersonatedUser({
        userId,
        companyId,
        claims: result.claims,
        target: result.target || null,
      });
      setImpersonationSession({
        userId,
        companyId,
        target: result.target || null,
      });
      await waitForImpersonationExitMin(startedAt);
      return result;
    } catch (err) {
      setHandoffPhase(null);
      setLoading(false);
      setError(err.message);
      throw err;
    }
  };

  const clearImpersonation = async (notes) => {
    const targetUserId =
      impersonatedUser?.userId ||
      (typeof window !== 'undefined'
        ? getImpersonationTargetMeta().userId
        : null);

    const targetCompanyId =
      impersonatedUser?.companyId ||
      (typeof window !== 'undefined'
        ? getImpersonationTargetMeta().companyId
        : null);

    const adminDestination = adminLocationHref(ADMIN_PATHS.DASHBOARD);
    const startedAt = Date.now();

    markImpersonationExiting();
    setHandoffPhase('ending');
    setLoading(true);
    setError(null);

    try {
      if (targetUserId) {
        const result = await revokeImpersonationSession(targetUserId, targetCompanyId, notes);
        if (!result.success) {
          throw new Error(result.error || 'Failed to end impersonation');
        }
      }

      await waitForImpersonationExitMin(startedAt);
      setImpersonatedUser(null);
      await signOutImpersonatedStaff();
      window.dispatchEvent(new Event('clearImpersonation'));
      window.location.replace(withImpersonationReturnParam(adminDestination, startedAt));

      return { success: true };
    } catch (err) {
      clearImpersonationExiting();
      setHandoffPhase(null);
      setLoading(false);
      setError(err.message);
      throw err;
    }
  };

  const checkImpersonationStatus = () => {
    return !!impersonatedUser;
  };

  const overlayMessage = handoffPhase === 'ending'
    ? 'Returning to admin panel...'
    : 'Signing into this account...';
  const overlayDetail = handoffPhase === 'starting'
    ? (impersonatedUser?.target?.name
      || impersonatedUser?.target?.email
      || getImpersonationTargetLabel()
      || 'Loading customer workspace')
    : null;

  return (
    <ImpersonationContext.Provider
      value={{
        impersonateUser,
        clearImpersonation,
        checkImpersonationStatus,
        impersonatedUser,
        loading,
        handoffPhase,
        error
      }}
    >
      {children}
      {loading && handoffPhase ? (
        <ImpersonationStartOverlay
          phase={handoffPhase}
          message={overlayMessage}
          detail={overlayDetail}
        />
      ) : null}
    </ImpersonationContext.Provider>
  );
};

export default ImpersonationProvider;
